import { useEffect, useState, useRef, useMemo, type Dispatch, type SetStateAction } from "react"
import { loadStripe } from "@stripe/stripe-js"
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { Button } from "~/react/ui/button"
import { Select } from "~/react/ui/select"
import { Checkbox } from "~/react/ui/checkbox"
import { Input } from "~/react/ui/input"
import type {
  User,
  DomainOwnerForm,
  DomainOwner,
  CountryOption,
  BillingInformation,
  CreditCard,
} from "~/react/components/email_accounts/types"
import visaLogo from "~/images/payment_cards/visa.svg"
import mastercardLogo from "~/images/payment_cards/mastercard.png"
import amexLogo from "~/images/payment_cards/amex.png"
import { Separator } from "@/react/ui/separator"
import { Icon } from "@/react/ui/icon"
import { Label } from "~/react/ui/label"
import { Hint } from "~/react/ui/hint"
import { FormHelper } from "~/helpers/form_helper"

export type { DomainOwnerForm, DomainOwner }

// Billing form type
interface BillingForm {
  companyName: string
  address: string
  postalCode: string
  city: string
  countryCode: string
  additionalInfo: string
}

// Payment form type
interface PaymentForm {
  nameOnCard: string
}

type Props = {
  user?: User
  domainOwner?: DomainOwner | null
  setDomainOwner: (owner: DomainOwner | null) => void
  domainOwnerConfirmed: boolean
  setDomainOwnerConfirmed: (v: boolean) => void
  editingDomainOwner: boolean
  setEditingDomainOwner: (v: boolean) => void
  domainOwnerForm: DomainOwnerForm
  setDomainOwnerForm: Dispatch<SetStateAction<DomainOwnerForm>>
  savingDomainOwner: boolean
  setSavingDomainOwner: (v: boolean) => void
  countryOptions?: CountryOption[]
  billingInformation?: BillingInformation
  creditCard?: CreditCard
  setBillingInformation: (info: BillingInformation | undefined) => void
  setCreditCard: (card: CreditCard | undefined) => void
  billingForm: BillingForm
  setBillingForm: Dispatch<SetStateAction<BillingForm>>
  paymentForm: PaymentForm
  setPaymentForm: Dispatch<SetStateAction<PaymentForm>>
  editingBilling: boolean
  setEditingBilling: (v: boolean) => void
  editingPayment: boolean
  setEditingPayment: (v: boolean) => void
  stripePublicKey?: string
}

// Helper to get country name from code
const getCountryName = (code: string, options: CountryOption[]) => {
  const country = options.find((c) => c.code === code)
  return country?.name || code
}

// Internal component that uses Stripe hooks (must be inside Elements provider)
function BillingTabContent({
  user,
  domainOwner,
  setDomainOwner,
  domainOwnerConfirmed,
  setDomainOwnerConfirmed,
  editingDomainOwner,
  setEditingDomainOwner,
  domainOwnerForm,
  setDomainOwnerForm,
  savingDomainOwner,
  setSavingDomainOwner,
  countryOptions = [],
  billingInformation,
  creditCard,
  setBillingInformation,
  setCreditCard,
  billingForm,
  setBillingForm,
  paymentForm,
  setPaymentForm,
  editingBilling,
  setEditingBilling,
  editingPayment,
  setEditingPayment,
}: Omit<Props, "stripePublicKey">) {
  const [internalError, setInternalError] = useState<string | undefined>(undefined)
  const [billingError, setBillingError] = useState<string | undefined>(undefined)
  const [paymentError, setPaymentError] = useState<string | undefined>(undefined)
  const [savingBilling, setSavingBilling] = useState<boolean>(false)
  const [savingPayment, setSavingPayment] = useState<boolean>(false)
  const googleMapsKey =
    typeof document !== "undefined"
      ? document.querySelector('meta[name="google-maps-key"]')?.getAttribute("content") || ""
      : ""
  const addressFieldRef = useRef<HTMLInputElement>(null)
  const postalCodeFieldRef = useRef<HTMLInputElement>(null)
  const cityFieldRef = useRef<HTMLInputElement>(null)

  // React Stripe hooks
  const stripe = useStripe()
  const elements = useElements()

  // Card element options using FormHelper for consistent styling
  const cardElementOptions = useMemo(() => FormHelper.elementStyles("13px"), [])

  // Convert country options to react-select format and sort alphabetically
  const countrySelectOptions = useMemo(() => {
    return countryOptions
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((country) => ({
        value: country.code,
        label: country.name,
        currency: country.currency,
      }))
  }, [countryOptions])

  // If there's no domain owner, show the form by default
  useEffect(() => {
    if (!domainOwner && !editingDomainOwner) {
      setEditingDomainOwner(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When editing starts and there's a domain owner, prefill the form
  useEffect(() => {
    if (editingDomainOwner && domainOwner) {
      setDomainOwnerForm({
        firstName: domainOwner.first_name || "",
        lastName: domainOwner.last_name || "",
        email: domainOwner.email || "",
        company: domainOwner.company || "",
        address: domainOwner.address || "",
        postalCode: domainOwner.postal_code || "",
        city: domainOwner.city || "",
        countryCode: domainOwner.country_code || "",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDomainOwner, domainOwner])

  // Sync React state when Google Maps autocomplete updates the hidden inputs
  useEffect(() => {
    if (!editingDomainOwner) return

    // Function to get hidden inputs (they're created by Stimulus controller)
    const getHiddenInputs = () => {
      return {
        city: document.getElementById("address-autocompleter-city") as HTMLInputElement | null,
        postalCode: document.getElementById("address-autocompleter-postal_code") as HTMLInputElement | null,
        route: document.getElementById("address-autocompleter-route") as HTMLInputElement | null,
        streetNumber: document.getElementById("address-autocompleter-street_number") as HTMLInputElement | null,
        country: document.getElementById("address-autocompleter-country_code") as HTMLInputElement | null,
      }
    }

    // When any hidden input changes, update React state from all hidden inputs
    const syncFromGoogleMaps = () => {
      const hidden = getHiddenInputs()

      // Build address from street number + route
      const streetNumber = hidden.streetNumber?.value || ""
      const route = hidden.route?.value || ""
      let address = [streetNumber, route].filter(Boolean).join(" ")

      // If no street address, check the visible address field (might have the place name)
      if (!address) {
        const addressField = document.getElementById("address-field") as HTMLInputElement | null
        address = addressField?.value || ""
      }

      // Get other values
      const postalCode = hidden.postalCode?.value || ""
      const city = hidden.city?.value || ""
      const country = hidden.country?.value || ""

      // Update React state with values from hidden inputs
      setDomainOwnerForm((prev) => {
        const updates: Partial<typeof prev> = {}
        if (prev.address !== address) updates.address = address
        if (prev.postalCode !== postalCode) updates.postalCode = postalCode
        if (prev.city !== city) updates.city = city
        if (prev.countryCode !== country) updates.countryCode = country

        if (Object.keys(updates).length > 0) {
          return { ...prev, ...updates }
        }
        return prev
      })
    }

    // Listen to address field to catch Google Maps updates
    const addressField = document.getElementById("address-field") as HTMLInputElement | null
    let isUserTyping = false
    let typingTimeout: NodeJS.Timeout | null = null
    let lastSyncedAddressValue = domainOwnerForm.address

    if (addressField) {
      const handleUserInput = () => {
        isUserTyping = true
        lastSyncedAddressValue = domainOwnerForm.address
        if (typingTimeout) {
          clearTimeout(typingTimeout)
        }
        typingTimeout = setTimeout(() => {
          isUserTyping = false
        }, 500)
      }

      const handleAddressFieldChange = () => {
        if (!isUserTyping) {
          const currentValue = addressField.value || ""
          if (currentValue && currentValue !== lastSyncedAddressValue && currentValue !== domainOwnerForm.address) {
            lastSyncedAddressValue = currentValue
            setDomainOwnerForm((prev) => {
              if (prev.address !== currentValue) {
                return { ...prev, address: currentValue }
              }
              return prev
            })
            setTimeout(() => {
              syncFromGoogleMaps()
            }, 50)
          } else if (currentValue === lastSyncedAddressValue) {
            setTimeout(() => {
              syncFromGoogleMaps()
            }, 100)
          }
        }
      }

      addressField.addEventListener("input", handleUserInput)
      addressField.addEventListener("change", handleAddressFieldChange)

      let pollingInterval: NodeJS.Timeout | null = null
      const startPolling = () => {
        if (pollingInterval) return
        pollingInterval = setInterval(() => {
          if (!isUserTyping && addressField) {
            const currentValue = addressField.value || ""
            if (currentValue && currentValue !== lastSyncedAddressValue && currentValue !== domainOwnerForm.address) {
              handleAddressFieldChange()
            }
          }
        }, 300)
      }

      const pollStartTimeout = setTimeout(() => {
        startPolling()
      }, 1000)

      const googleMapsCleanup = () => {
        if (pollingInterval) {
          clearInterval(pollingInterval)
          pollingInterval = null
        }
        if (typingTimeout) {
          clearTimeout(typingTimeout)
        }
        clearTimeout(pollStartTimeout)
        addressField.removeEventListener("input", handleUserInput)
        addressField.removeEventListener("change", handleAddressFieldChange)
      }

      ;(addressField as any).__reactCleanup = googleMapsCleanup
    }

    const setupListeners = () => {
      const hidden = getHiddenInputs()
      const listeners: Array<{
        element: HTMLInputElement
        handler: () => void
      }> = []

      if (hidden.city) {
        hidden.city.addEventListener("change", syncFromGoogleMaps)
        listeners.push({ element: hidden.city, handler: syncFromGoogleMaps })
      }
      if (hidden.postalCode) {
        hidden.postalCode.addEventListener("change", syncFromGoogleMaps)
        listeners.push({
          element: hidden.postalCode,
          handler: syncFromGoogleMaps,
        })
      }
      if (hidden.route) {
        hidden.route.addEventListener("change", syncFromGoogleMaps)
        listeners.push({ element: hidden.route, handler: syncFromGoogleMaps })
      }
      if (hidden.streetNumber) {
        hidden.streetNumber.addEventListener("change", syncFromGoogleMaps)
        listeners.push({
          element: hidden.streetNumber,
          handler: syncFromGoogleMaps,
        })
      }
      if (hidden.country) {
        hidden.country.addEventListener("change", syncFromGoogleMaps)
        listeners.push({ element: hidden.country, handler: syncFromGoogleMaps })
      }

      return () => {
        listeners.forEach(({ element, handler }) => {
          element.removeEventListener("change", handler)
        })
      }
    }

    let cleanup = setupListeners()
    const timeoutId = setTimeout(() => {
      cleanup()
      cleanup = setupListeners()
    }, 200)

    return () => {
      cleanup()
      clearTimeout(timeoutId)
      const addressField = document.getElementById("address-field") as HTMLInputElement | null
      if (addressField && (addressField as any).__reactCleanup) {
        ;(addressField as any).__reactCleanup()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDomainOwner])

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setInternalError(undefined)
    setSavingDomainOwner(true)
    try {
      // Read latest values from hidden inputs (Google Maps might have updated them)
      const hiddenCity = document.getElementById("address-autocompleter-city") as HTMLInputElement | null
      const hiddenPostalCode = document.getElementById("address-autocompleter-postal_code") as HTMLInputElement | null
      const hiddenRoute = document.getElementById("address-autocompleter-route") as HTMLInputElement | null
      const hiddenStreetNumber = document.getElementById(
        "address-autocompleter-street_number",
      ) as HTMLInputElement | null
      const hiddenCountry = document.getElementById("address-autocompleter-country_code") as HTMLInputElement | null

      const streetNumber = hiddenStreetNumber?.value || ""
      const route = hiddenRoute?.value || ""
      const addressFromHidden = [streetNumber, route].filter(Boolean).join(" ")

      const finalAddress = addressFromHidden || domainOwnerForm.address
      const finalPostalCode = hiddenPostalCode?.value || domainOwnerForm.postalCode
      const finalCity = hiddenCity?.value || domainOwnerForm.city
      const finalCountry = hiddenCountry?.value || domainOwnerForm.countryCode

      const payload = {
        managed_domain_owner: {
          company: domainOwnerForm.company,
          first_name: domainOwnerForm.firstName,
          last_name: domainOwnerForm.lastName,
          email: domainOwnerForm.email,
          address: finalAddress,
          postal_code: finalPostalCode,
          city: finalCity,
          country_code: finalCountry,
        },
      }
      const resp = await fetch("/managed_domain_owners", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify(payload),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        setInternalError(
          (data && (data.message as string)) || i18n.t("js.email_accounts.provisioning.domain_owner.generic_error"),
        )
        return
      }
      const savedDomainOwner = data as DomainOwner

      setDomainOwnerForm({
        firstName: savedDomainOwner.first_name || "",
        lastName: savedDomainOwner.last_name || "",
        email: savedDomainOwner.email || "",
        company: savedDomainOwner.company || "",
        address: savedDomainOwner.address || "",
        postalCode: savedDomainOwner.postal_code || "",
        city: savedDomainOwner.city || "",
        countryCode: savedDomainOwner.country_code || "",
      })
      setDomainOwner(savedDomainOwner)

      const addressFieldElement = document.getElementById("address-field") as HTMLInputElement | null
      if (addressFieldElement && savedDomainOwner.address) {
        addressFieldElement.value = savedDomainOwner.address
      }

      if (hiddenRoute && savedDomainOwner.address) {
        hiddenRoute.value = savedDomainOwner.address
      }
      if (hiddenPostalCode && savedDomainOwner.postal_code) {
        hiddenPostalCode.value = savedDomainOwner.postal_code
      }
      if (hiddenCity && savedDomainOwner.city) {
        hiddenCity.value = savedDomainOwner.city
      }
      if (hiddenCountry && savedDomainOwner.country_code) {
        hiddenCountry.value = savedDomainOwner.country_code
      }

      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              resolve(undefined)
            })
          })
        })
      })

      setEditingDomainOwner(false)
      setDomainOwnerConfirmed(true)
    } catch {
      setInternalError(i18n.t("js.email_accounts.provisioning.domain_owner.generic_error"))
    } finally {
      setSavingDomainOwner(false)
    }
  }

  const handleCancelDomainOwnerEdit = () => {
    setEditingDomainOwner(false)
    setInternalError(undefined)
    if (domainOwner) {
      setDomainOwnerForm({
        firstName: domainOwner.first_name || "",
        lastName: domainOwner.last_name || "",
        email: domainOwner.email || "",
        company: domainOwner.company || "",
        address: domainOwner.address || "",
        postalCode: domainOwner.postal_code || "",
        city: domainOwner.city || "",
        countryCode: domainOwner.country_code || "",
      })
    } else {
      setDomainOwnerForm({
        firstName: user?.first_name || "",
        lastName: user?.last_name || "",
        email: user?.email || "",
        company: "",
        address: "",
        postalCode: "",
        city: "",
        countryCode: "",
      })
    }
  }

  const handleBillingSave = async () => {
    setBillingError(undefined)
    setSavingBilling(true)
    try {
      const payload = {
        billing_information: {
          company: billingForm.companyName,
          address: billingForm.address,
          postal_code: billingForm.postalCode,
          city: billingForm.city,
          country_code: billingForm.countryCode,
          additional_information: billingForm.additionalInfo || "",
        },
      }

      const resp = await fetch("/billing-information", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify(payload),
      })

      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        setBillingError(
          (data && (data.message as string)) || i18n.t("js.email_accounts.provisioning.domain_owner.generic_error"),
        )
        return
      }

      // Update billing information state from form data
      setBillingInformation({
        company_name: billingForm.companyName,
        address: billingForm.address,
        postal_code: billingForm.postalCode,
        city: billingForm.city,
        country_code: billingForm.countryCode,
        country_name:
          countryOptions.find((c) => c.code === billingForm.countryCode)?.name || billingInformation?.country_name,
        additional_info: billingForm.additionalInfo,
      })

      // Success - close the form
      setEditingBilling(false)
    } catch {
      setBillingError(i18n.t("js.email_accounts.provisioning.domain_owner.generic_error"))
    } finally {
      setSavingBilling(false)
    }
  }

  const handlePaymentSave = async () => {
    setPaymentError(undefined)
    setSavingPayment(true)
    try {
      if (!stripe || !elements) {
        setPaymentError("Stripe not initialized. Please try again.")
        return
      }

      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        setPaymentError("Card element not found. Please try again.")
        return
      }

      // Create payment method from card element
      const { error: createError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: paymentForm.nameOnCard,
        },
      })

      if (createError) {
        setPaymentError(createError.message || "Failed to create payment method")
        return
      }

      if (!paymentMethod) {
        setPaymentError("Failed to create payment method")
        return
      }

      // Save payment method to backend
      const url = creditCard?.id ? `/credit-cards/${creditCard.id}` : "/credit-cards"
      const method = creditCard?.id ? "PUT" : "POST"
      const resp = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          payment_method_id: paymentMethod.id,
        }),
      })

      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        setPaymentError(
          (data && (data.error as string)) ||
            (data && (data.message as string)) ||
            i18n.t("js.email_accounts.provisioning.domain_owner.generic_error"),
        )
        return
      }

      // Update credit card state from payment method data
      const card = paymentMethod.card
      if (card) {
        setCreditCard({
          id: creditCard?.id,
          card_type: card.brand,
          last_4: card.last4,
          full_name: paymentForm.nameOnCard,
          expiration_month: card.exp_month?.toString(),
          expiration_year: card.exp_year?.toString(),
          payment_method_id: paymentMethod.id,
        })
      } else {
        // Fallback: if card data is not available, at least set the payment_method_id
        setCreditCard({
          ...(creditCard || {}),
          payment_method_id: paymentMethod.id,
        })
      }

      // Success - close the form
      setEditingPayment(false)
    } catch (error) {
      setPaymentError(i18n.t("js.email_accounts.provisioning.domain_owner.generic_error"))
    } finally {
      setSavingPayment(false)
    }
  }

  return (
    <div className="email-accounts-provisioning">
      {/* Review Information Header */}
      <h2 className="text-lg-medium mb-1">{i18n.t("js.email_accounts.provisioning.domain_owner.review_info_title")}</h2>
      <p className="text-xs text-body-secondary mb-6">
        {i18n.t("js.email_accounts.provisioning.domain_owner.review_info_subtitle")}
      </p>

      {/* Domain Owner Information */}
      <div className="mb-4">
        <h3 className="text-md-medium mb-1">
          {i18n.t("js.email_accounts.provisioning.domain_owner.domain_owner_title")}
        </h3>
        <p className="text-xs text-body-secondary mb-4">
          {i18n.t("js.email_accounts.provisioning.domain_owner.domain_owner_subtitle")}
        </p>

        {!editingDomainOwner ? (
          <>
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-1">
                {domainOwner ? (
                  <div>
                    <span className="text-sm-medium">
                      {domainOwner.first_name} {domainOwner.last_name}
                    </span>
                    <span className="text-xs ml-2">{domainOwner.email}</span>
                  </div>
                ) : (
                  <span className="text-xs text-hint">
                    {i18n.t("js.email_accounts.provisioning.domain_owner.no_domain_owner_information")}
                  </span>
                )}
                <Button type="button" size="xs" onClick={() => setEditingDomainOwner(true)}>
                  {i18n.t("js.email_accounts.provisioning.domain_owner.edit_information")}
                </Button>
              </div>
              {domainOwner ? (
                <>
                  <p className="text-xs">
                    {domainOwner.company && (
                      <>
                        {domainOwner.company}
                        <br />
                      </>
                    )}
                    {domainOwner.address}
                    <br />
                    {domainOwner.city}, {domainOwner.state || getCountryName(domainOwner.country_code, countryOptions)}{" "}
                    {domainOwner.postal_code}
                  </p>
                </>
              ) : (
                <span className="text-xs text-hint">
                  {i18n.t("js.email_accounts.provisioning.domain_owner.no_domain_owner_information")}
                </span>
              )}
            </div>

            {/* Confirmation checkbox */}
            {domainOwner && (
              <div className="h-form-group align-items-center">
                <div className="h-form-group__input">
                  <Checkbox
                    id="domain-owner-confirmed"
                    checked={domainOwnerConfirmed}
                    onChange={(e) => setDomainOwnerConfirmed(e.target.checked)}
                  />
                </div>
                <div className="h-form-group__infos">
                  <Label htmlFor="domain-owner-confirmed" className="mb-0">
                    {i18n.t("js.email_accounts.provisioning.domain_owner.confirm_details")}
                    &nbsp;
                    <span className="text-2xs text-hint">
                      {i18n.t("js.email_accounts.provisioning.domain_owner.required")}
                    </span>
                  </Label>
                </div>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handleFormSubmit}>
            {internalError && (
              <Hint variant="error" className="mb-3">
                {internalError}
              </Hint>
            )}
            <div className="row g-4 mb-4">
              <div className="col-6">
                <Label className="mb-1" htmlFor="domain-owner-first-name">
                  {i18n.t("js.email_accounts.provisioning.domain_owner.first_name")}
                </Label>
                <Input
                  id="domain-owner-first-name"
                  type="text"
                  fullWidth
                  value={domainOwnerForm.firstName}
                  onChange={(e) =>
                    setDomainOwnerForm((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="col-6">
                <Label className="mb-1" htmlFor="domain-owner-last-name">
                  {i18n.t("js.email_accounts.provisioning.domain_owner.last_name")}
                </Label>
                <Input
                  id="domain-owner-last-name"
                  type="text"
                  fullWidth
                  value={domainOwnerForm.lastName}
                  onChange={(e) =>
                    setDomainOwnerForm((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                  required
                />
              </div>
            </div>
            <div className="row g-4 mb-4">
              <div className="col-6">
                <Label className="mb-1" htmlFor="domain-owner-email">
                  {i18n.t("js.email_accounts.provisioning.domain_owner.email")}
                </Label>
                <Input
                  id="domain-owner-email"
                  type="email"
                  fullWidth
                  value={domainOwnerForm.email}
                  onChange={(e) =>
                    setDomainOwnerForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="col-6">
                <Label className="mb-1" htmlFor="domain-owner-company">
                  {i18n.t("js.email_accounts.provisioning.domain_owner.company_name")}
                </Label>
                <Input
                  id="domain-owner-company"
                  type="text"
                  fullWidth
                  value={domainOwnerForm.company}
                  onChange={(e) =>
                    setDomainOwnerForm((prev) => ({
                      ...prev,
                      company: e.target.value,
                    }))
                  }
                  required
                  autoComplete="organization"
                />
              </div>
            </div>
            <div data-controller="address-autocompleter" data-address-autocompleter-key-value={googleMapsKey}>
              <div data-address-autocompleter-target="details">
                <div className="row g-4 mb-4">
                  <div className="col-8">
                    <Label className="mb-1" htmlFor="address-field">
                      {i18n.t("js.email_accounts.provisioning.domain_owner.address")}
                    </Label>
                    <Input
                      ref={addressFieldRef}
                      id="address-field"
                      type="text"
                      fullWidth
                      value={domainOwnerForm.address}
                      onChange={(e) =>
                        setDomainOwnerForm((prev) => ({
                          ...prev,
                          address: e.target.value,
                        }))
                      }
                      required
                      placeholder={i18n.t("js.email_accounts.provisioning.domain_owner.address_placeholder")}
                      autoComplete="street-address"
                      data-address-autocompleter-target="address"
                    />
                  </div>
                  <div className="col-4">
                    <Label className="mb-1" htmlFor="domain-owner-postal-code">
                      {i18n.t("js.email_accounts.provisioning.domain_owner.postal_code")}
                    </Label>
                    <Input
                      ref={postalCodeFieldRef}
                      id="domain-owner-postal-code"
                      type="text"
                      fullWidth
                      value={domainOwnerForm.postalCode}
                      onChange={(e) =>
                        setDomainOwnerForm((prev) => ({
                          ...prev,
                          postalCode: e.target.value,
                        }))
                      }
                      required
                      autoComplete="postal-code"
                      data-address-autocompleter-target="postalcode"
                    />
                  </div>
                </div>
                <div className="row g-4 mb-4">
                  <div className="col-6">
                    <Label className="mb-1" htmlFor="domain-owner-city">
                      {i18n.t("js.email_accounts.provisioning.domain_owner.city")}
                    </Label>
                    <Input
                      ref={cityFieldRef}
                      id="domain-owner-city"
                      type="text"
                      fullWidth
                      value={domainOwnerForm.city}
                      onChange={(e) =>
                        setDomainOwnerForm((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      required
                      autoComplete="address-level2"
                      data-address-autocompleter-target="city"
                    />
                  </div>
                  <div className="col-6">
                    <Label className="mb-1" htmlFor="domain-owner-country">
                      {i18n.t("js.email_accounts.provisioning.domain_owner.country")}
                    </Label>
                    <div id="domain-owner-country">
                      <Select
                        options={countrySelectOptions}
                        placeholder={i18n.t("js.email_accounts.provisioning.domain_owner.select_country")}
                        value={countrySelectOptions.find((opt) => opt.value === domainOwnerForm.countryCode) || null}
                        onChange={(selected: any) => {
                          const countryCode = selected ? selected.value : ""
                          setDomainOwnerForm((prev) => ({
                            ...prev,
                            countryCode,
                          }))
                          const hiddenCountry = document.getElementById(
                            "address-autocompleter-country_code",
                          ) as HTMLInputElement | null
                          if (hiddenCountry) {
                            hiddenCountry.value = countryCode
                            hiddenCountry.dispatchEvent(new Event("change", { bubbles: true }))
                          }
                        }}
                        isClearable={false}
                        isSearchable={true}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        styles={{
                          control: (baseStyles) => ({
                            ...baseStyles,
                            boxShadow: "none",
                          }),
                          option: (baseStyles) => ({
                            ...baseStyles,
                            padding: "var(--spacing-02) var(--spacing-04)",
                            minHeight: "auto",
                          }),
                        }}
                        classNames={{
                          control: () => "selectize-input",
                          valueContainer: () => "p-0",
                          option: () => "option",
                        }}
                      />
                      <input
                        type="hidden"
                        id="address-autocompleter-country_code"
                        data-address-autocompleter-target="countrycode"
                        value={domainOwnerForm.countryCode}
                        onChange={() => {}}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button type="submit" className="flex-grow-1" disabled={savingDomainOwner}>
                {i18n.t("js.email_accounts.provisioning.domain_owner.save")}
              </Button>
              <Button type="button" variant="ghost" onClick={handleCancelDomainOwnerEdit} disabled={savingDomainOwner}>
                {i18n.t("js.email_accounts.provisioning.domain_owner.cancel")}
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Divider */}
      <Separator className="my-8" />

      {/* Billing Information */}
      <h3 className="text-md-medium mb-1">
        {i18n.t("js.email_accounts.provisioning.domain_owner.billing_info_title")}
      </h3>
      <p className="text-xs text-body-secondary mb-4">
        {i18n.t("js.email_accounts.provisioning.domain_owner.billing_info_subtitle")}
      </p>

      {!editingBilling && billingInformation ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-1">
            {billingInformation.company_name && (
              <div className="text-sm-medium mb-1">{billingInformation.company_name}</div>
            )}
            <Button
              type="button"
              size="xs"
              onClick={() => {
                setEditingBilling(true)
                // Prefill form with existing data
                setBillingForm({
                  companyName: billingInformation.company_name || "",
                  address: billingInformation.address || "",
                  postalCode: billingInformation.postal_code || "",
                  city: billingInformation.city || "",
                  countryCode: billingInformation.country_code || "",
                  additionalInfo: billingInformation.additional_info || "",
                })
              }}
            >
              {i18n.t("js.email_accounts.provisioning.domain_owner.edit_information")}
            </Button>
          </div>
          <div className="text-xs">
            {billingInformation.address && <div>{billingInformation.address}</div>}
            {(billingInformation.city || billingInformation.country_name || billingInformation.postal_code) && (
              <div>
                {billingInformation.city}
                {billingInformation.country_name ? `, ${billingInformation.country_name}` : ""}{" "}
                {billingInformation.postal_code}
              </div>
            )}
            {billingInformation.additional_info && (
              <>
                <div className="text-xs-medium text-hint mt-2">
                  {i18n.t("js.email_accounts.provisioning.domain_owner.additional_info")}
                </div>
                {billingInformation.additional_info}
              </>
            )}
          </div>
        </>
      ) : (
        /* Billing Form - shown when editing or no billing info exists */
        <>
          <div className="mb-4">
            <Label htmlFor="billing-company">
              {i18n.t("js.email_accounts.provisioning.domain_owner.company_name")}
            </Label>
            <Input
              id="billing-company"
              type="text"
              fullWidth
              value={billingForm.companyName}
              onChange={(e) =>
                setBillingForm((prev) => ({
                  ...prev,
                  companyName: e.target.value,
                }))
              }
            />
          </div>
          <div className="row g-4 mb-4">
            <div className="col-8">
              <Label htmlFor="billing-address">{i18n.t("js.email_accounts.provisioning.domain_owner.address")}</Label>
              <Input
                id="billing-address"
                type="text"
                fullWidth
                value={billingForm.address}
                onChange={(e) =>
                  setBillingForm((prev) => ({
                    ...prev,
                    address: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-4">
              <Label htmlFor="billing-postal-code">
                {i18n.t("js.email_accounts.provisioning.domain_owner.postal_code")}
              </Label>
              <Input
                id="billing-postal-code"
                type="text"
                fullWidth
                value={billingForm.postalCode}
                onChange={(e) =>
                  setBillingForm((prev) => ({
                    ...prev,
                    postalCode: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <div className="row g-4 mb-4">
            <div className="col-6">
              <Label htmlFor="billing-city">{i18n.t("js.email_accounts.provisioning.domain_owner.city")}</Label>
              <Input
                id="billing-city"
                type="text"
                fullWidth
                value={billingForm.city}
                onChange={(e) => setBillingForm((prev) => ({ ...prev, city: e.target.value }))}
              />
            </div>
            <div className="col-6">
              <Label htmlFor="billing-country">{i18n.t("js.email_accounts.provisioning.domain_owner.country")}</Label>
              <Select
                options={countrySelectOptions}
                placeholder={i18n.t("js.email_accounts.provisioning.domain_owner.select_country")}
                value={countrySelectOptions.find((opt) => opt.value === billingForm.countryCode) || null}
                onChange={(selected: any) => {
                  setBillingForm((prev) => ({
                    ...prev,
                    countryCode: selected ? selected.value : "",
                  }))
                }}
                isClearable={false}
                isSearchable={true}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    boxShadow: "none",
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    padding: "var(--spacing-02) var(--spacing-04)",
                    minHeight: "auto",
                  }),
                }}
                classNames={{
                  control: () => "selectize-input",
                  valueContainer: () => "p-0",
                  option: () => "option",
                }}
              />
            </div>
          </div>
          <div className="mb-4">
            <Label htmlFor="billing-additional">
              {i18n.t("js.email_accounts.provisioning.domain_owner.additional_info")}{" "}
              <span className="fw-normal text-hint">
                ({i18n.t("js.email_accounts.provisioning.domain_owner.optional")})
              </span>
            </Label>
            <Input
              id="billing-additional"
              type="text"
              fullWidth
              value={billingForm.additionalInfo}
              onChange={(e) =>
                setBillingForm((prev) => ({
                  ...prev,
                  additionalInfo: e.target.value,
                }))
              }
            />
            <Hint className="mt-1">{i18n.t("js.email_accounts.provisioning.domain_owner.additional_info_hint")}</Hint>
          </div>
          {editingBilling && (
            <>
              {billingError && (
                <Hint variant="error" className="mb-3">
                  {billingError}
                </Hint>
              )}
              <div className="d-flex gap-2 mt-4 mb-8">
                <Button type="button" className="flex-grow-1" onClick={handleBillingSave} disabled={savingBilling}>
                  {savingBilling
                    ? i18n.t("js.email_accounts.provisioning.billing.saving")
                    : i18n.t("js.email_accounts.provisioning.billing.save")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setEditingBilling(false)
                    setBillingError(undefined)
                    // Reset form to saved values
                    if (billingInformation) {
                      setBillingForm({
                        companyName: billingInformation.company_name || "",
                        address: billingInformation.address || "",
                        postalCode: billingInformation.postal_code || "",
                        city: billingInformation.city || "",
                        countryCode: billingInformation.country_code || "",
                        additionalInfo: billingInformation.additional_info || "",
                      })
                    }
                  }}
                  disabled={savingBilling}
                >
                  {i18n.t("js.email_accounts.provisioning.domain_owner.cancel")}
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {/* Payment Method */}
      <div className="mt-6">
        {!editingPayment && creditCard ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="text-sm-medium">
                {i18n.t("js.email_accounts.provisioning.domain_owner.payment_method.title")}
              </h3>
              <Button
                type="button"
                size="xs"
                onClick={() => {
                  setEditingPayment(true)
                  setPaymentForm({
                    nameOnCard: creditCard.full_name || "",
                  })
                }}
              >
                {i18n.t("js.email_accounts.provisioning.domain_owner.payment_method.edit")}
              </Button>
            </div>
            <div className="d-flex gap-2">
              <div className="payment-card-logo">
                {creditCard.card_type?.toLowerCase() === "visa" ? (
                  <img src={visaLogo} alt="Visa" />
                ) : creditCard.card_type?.toLowerCase() === "mastercard" ? (
                  <img src={mastercardLogo} alt="Mastercard" />
                ) : creditCard.card_type?.toLowerCase() === "amex" ||
                  creditCard.card_type?.toLowerCase() === "american_express" ? (
                  <img src={amexLogo} alt="American Express" />
                ) : (
                  <span className="text-xs-medium text-dark text-uppercase">
                    {creditCard.card_type?.slice(0, 4) || "Card"}
                  </span>
                )}
              </div>
              <div className="text-xs mt-05">
                <div className="fw-medium">
                  {creditCard.card_type
                    ? `${creditCard.card_type.charAt(0).toUpperCase()}${creditCard.card_type.slice(1).toLowerCase()}`
                    : ""}{" "}
                  **** {creditCard.last_4}
                </div>
                <div className="text-body-secondary mt-1">
                  {creditCard.full_name} · Expires {creditCard.expiration_month}/{creditCard.expiration_year}
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Payment Form - shown when editing or no credit card exists */
          <>
            <h3 className="text-sm-medium mb-4">
              {i18n.t("js.email_accounts.provisioning.domain_owner.payment_method.title")}
            </h3>
            <div className="mb-2">
              <Label htmlFor="name-on-card">
                {i18n.t("js.email_accounts.provisioning.domain_owner.payment_method.name_on_card")}
              </Label>
              <Input
                id="name-on-card"
                type="text"
                fullWidth
                value={paymentForm.nameOnCard}
                onChange={(e) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    nameOnCard: e.target.value,
                  }))
                }
                placeholder="Name on card"
              />
            </div>
            <fieldset className="mb-3">
              <legend className="h-label">
                {i18n.t("js.email_accounts.provisioning.domain_owner.payment_method.card_number")}
              </legend>
              <div id="card-field" className="stripe-card-field">
                <CardElement options={cardElementOptions} />
              </div>
              <Hint className="mt-1">
                <Icon icon="lock-alt" size="xs" className="mr-1" />
                {i18n.t("js.email_accounts.provisioning.domain_owner.payment_method.secure_message")}
              </Hint>
            </fieldset>
            {editingPayment && (
              <>
                {paymentError && (
                  <Hint variant="error" className="mb-3">
                    {paymentError}
                  </Hint>
                )}
                <div className="d-flex gap-2 mt-4">
                  <Button type="button" onClick={handlePaymentSave} disabled={savingPayment} className="flex-grow-1">
                    {savingPayment
                      ? i18n.t("js.email_accounts.provisioning.domain_owner.payment_method.saving")
                      : i18n.t("js.email_accounts.provisioning.domain_owner.payment_method.save")}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setEditingPayment(false)
                      setPaymentError(undefined)
                      // Reset form to saved values
                      if (creditCard) {
                        setPaymentForm({
                          nameOnCard: creditCard.full_name || "",
                        })
                      }
                    }}
                    disabled={savingPayment}
                  >
                    {i18n.t("js.email_accounts.provisioning.domain_owner.payment_method.cancel")}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Wrapper component that provides the Stripe Elements context
export default function BillingTab({ stripePublicKey, ...props }: Props) {
  // Initialize Stripe promise once
  const stripePromise = useMemo(() => {
    if (!stripePublicKey) return null
    return loadStripe(stripePublicKey)
  }, [stripePublicKey])

  // Always wrap in Elements provider, even if stripePromise is null
  // This allows useStripe() and useElements() to be called safely
  // They will return null when Stripe is not initialized
  return (
    <Elements stripe={stripePromise || null}>
      <BillingTabContent {...props} />
    </Elements>
  )
}
